import React, { useState, useEffect } from 'react';
import { FoxSpinnerInline } from '../ui/FoxSpinner';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DollarSign, Percent, PiggyBank, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';

const FinancesManagement = () => {
  const [config, setConfig] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [taxPercentage, setTaxPercentage] = useState('2.8');
  const [reserveType, setReserveType] = useState('percentage');
  const [reserveValue, setReserveValue] = useState('0');
  
  // Taxas por gateway
  const [gatewayFees, setGatewayFees] = useState({
    efi_fee_percentage: '0.99',
    efi_iof_percentage: '0',
    stripe_fee_percentage: '1.19',
    stripe_iof_percentage: '3.5',
    mercadopago_fee_percentage: '0.99',
    mercadopago_iof_percentage: '0',
    mbway_fee_percentage: '3.0',
    mbway_fee_fixed_eur: '0.25'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      
      const [configRes, summaryRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/finance/config`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/finance/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch((e) => {
          // resumo lento (banco) não pode derrubar a tela de configuração de taxas
          toast.error(e?.response?.status === 503
            ? 'Resumo financeiro: banco demorou para responder. Tente novamente em instantes.'
            : 'Erro ao carregar resumo financeiro');
          return { data: null };
        })
      ]);
      
      setConfig(configRes.data);
      setSummary(summaryRes.data);
      
      // Preenche form
      setTaxPercentage(configRes.data.tax_percentage.toString());
      setReserveType(configRes.data.reserve_type);
      setReserveValue(
        configRes.data.reserve_type === 'percentage' 
          ? configRes.data.reserve_percentage.toString()
          : configRes.data.reserve_fixed.toString()
      );
      
      // Preenche taxas por gateway
      if (configRes.data.gateway_fees) {
        const gf = configRes.data.gateway_fees;
        setGatewayFees({
          efi_fee_percentage: (gf.efi_fee_percentage ?? 0.99).toString(),
          efi_iof_percentage: (gf.efi_iof_percentage ?? 0).toString(),
          stripe_fee_percentage: (gf.stripe_fee_percentage ?? 1.19).toString(),
          stripe_iof_percentage: (gf.stripe_iof_percentage ?? 3.5).toString(),
          mercadopago_fee_percentage: (gf.mercadopago_fee_percentage ?? 0.99).toString(),
          mercadopago_iof_percentage: (gf.mercadopago_iof_percentage ?? 0).toString(),
          mbway_fee_percentage: (gf.mbway_fee_percentage ?? 3.0).toString(),
          mbway_fee_fixed_eur: (gf.mbway_fee_fixed_eur ?? 0.25).toString()
        });
      }
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTax = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      
      await axios.put(`${BACKEND_URL}/api/finance/config`, {
        tax_percentage: parseFloat(taxPercentage)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Imposto atualizado!');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar imposto');
    }
  };

  const handleUpdateReserve = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      
      const data = {
        reserve_type: reserveType
      };
      
      if (reserveType === 'percentage') {
        data.reserve_percentage = parseFloat(reserveValue);
        data.reserve_fixed = 0;
      } else {
        data.reserve_fixed = parseFloat(reserveValue);
        data.reserve_percentage = 0;
      }
      
      await axios.put(`${BACKEND_URL}/api/finance/config`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Reserva atualizada!');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar reserva');
    }
  };

  const handleUpdateGatewayFees = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
      
      await axios.put(`${BACKEND_URL}/api/finance/config`, {
        gateway_fees: {
          efi_fee_percentage: parseFloat(gatewayFees.efi_fee_percentage),
          efi_iof_percentage: parseFloat(gatewayFees.efi_iof_percentage),
          stripe_fee_percentage: parseFloat(gatewayFees.stripe_fee_percentage),
          stripe_iof_percentage: parseFloat(gatewayFees.stripe_iof_percentage),
          mercadopago_fee_percentage: parseFloat(gatewayFees.mercadopago_fee_percentage),
          mercadopago_iof_percentage: parseFloat(gatewayFees.mercadopago_iof_percentage),
          mbway_fee_percentage: parseFloat(gatewayFees.mbway_fee_percentage),
          mbway_fee_fixed_eur: parseFloat(gatewayFees.mbway_fee_fixed_eur)
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Taxas de gateway atualizadas!');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar taxas');
    }
  };

  if (loading) {
    return <div className="flex justify-center p-8"><FoxSpinnerInline size="lg" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="finances-management">
      <div>
        <h2 className="text-2xl font-bold">💰 Finanças</h2>
        <p className="text-gray-600">Gerencie impostos e reservas do sistema</p>
      </div>

      {/* Cards de Resumo */}
      {summary && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="border-blue-300">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Lucro Líquido</CardTitle>
              <TrendingDown className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">
                R$ {summary.net_profit.toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground">
                Vendas − moedas − taxas − imposto
              </p>
            </CardContent>
          </Card>

          <Card className="border-red-300">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Imposto ({summary.tax_percentage}%)</CardTitle>
              <DollarSign className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                R$ {summary.tax_amount.toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground">
                Sobre o lucro bruto
              </p>
            </CardContent>
          </Card>

          <Card className="border-orange-300">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Reserva Total</CardTitle>
              <PiggyBank className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">
                R$ {summary.total_reserved.toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground">
                Acumulado
              </p>
            </CardContent>
          </Card>

          <Card className="border-green-300">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Lucro Final</CardTitle>
              <TrendingDown className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                R$ {summary.final_profit.toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground">
                Após imposto, reserva e recompensas
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="gateway" className="space-y-4">
        <div className="overflow-x-auto">
          <TabsList className="inline-flex w-auto min-w-full">
            <TabsTrigger value="gateway">
              <DollarSign className="w-4 h-4 mr-2" />
              Taxas Gateway
            </TabsTrigger>
            <TabsTrigger value="tax">
              <Percent className="w-4 h-4 mr-2" />
              Imposto
            </TabsTrigger>
            <TabsTrigger value="reserve">
              <PiggyBank className="w-4 h-4 mr-2" />
              Reserva
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Aba Imposto */}
        <TabsContent value="tax">
          <Card>
            <CardHeader>
              <CardTitle>Configuração de Imposto</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Percentual de Imposto sobre o Lucro Bruto (%)</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={taxPercentage}
                    onChange={(e) => setTaxPercentage(e.target.value)}
                    className="max-w-xs"
                  />
                  <Button onClick={handleUpdateTax} className="bg-red-600 hover:bg-red-700">
                    Salvar
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  O imposto é calculado sobre o <b>lucro bruto</b> (vendas − custo das moedas).
                  Recompensas e reservas entram depois, no Lucro Final.
                </p>
              </div>

              {summary && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-bold text-sm text-red-600 mb-2">Cálculo Atual:</h4>
                  <div className="space-y-1 text-sm">
                    <p>Vendas no período: <strong>R$ {(summary.revenue ?? 0).toFixed(2)}</strong></p>
                    <p>Custo das moedas: <strong>− R$ {(summary.coins_cost ?? 0).toFixed(2)}</strong></p>
                    <p>Lucro Bruto: <strong>R$ {(summary.gross_profit ?? 0).toFixed(2)}</strong></p>
                    <p>Imposto ({summary.tax_percentage}% do lucro bruto): <strong className="text-red-600">R$ {summary.tax_amount.toFixed(2)}</strong></p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Reserva */}
        <TabsContent value="reserve">
          <Card>
            <CardHeader>
              <CardTitle>Configuração de Reserva</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Tipo de Reserva</Label>
                <Select value={reserveType} onValueChange={setReserveType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentual (%)</SelectItem>
                    <SelectItem value="fixed">Valor Fixo (R$)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>
                  {reserveType === 'percentage' ? 'Percentual da Reserva (%)' : 'Valor Fixo por Pedido (R$)'}
                </Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step={reserveType === 'percentage' ? '0.1' : '0.01'}
                    min="0"
                    max={reserveType === 'percentage' ? '100' : undefined}
                    value={reserveValue}
                    onChange={(e) => setReserveValue(e.target.value)}
                    className="max-w-xs"
                  />
                  <Button onClick={handleUpdateReserve} className="bg-orange-600 hover:bg-orange-700">
                    Salvar
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  {reserveType === 'percentage' 
                    ? 'Percentual sobre o valor de cada pedido completo'
                    : 'Valor fixo reservado de cada pedido completo'
                  }
                </p>
              </div>

              {summary && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h4 className="font-bold text-sm text-orange-600 mb-2">Reserva Acumulada:</h4>
                  <div className="space-y-1 text-sm">
                    <p>Tipo: <strong>{reserveType === 'percentage' ? 'Percentual' : 'Valor Fixo'}</strong></p>
                    <p>Configuração: <strong>{summary.reserve_value}{reserveType === 'percentage' ? '%' : ' R$'}</strong></p>
                    <p>Total Reservado: <strong className="text-orange-600">R$ {summary.total_reserved.toFixed(2)}</strong></p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aba Taxas por Gateway */}
        <TabsContent value="gateway">
          <Card>
            <CardHeader>
              <CardTitle>Taxas por Gateway de Pagamento</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* EFI PIX */}
              <div className="p-4 border rounded-lg bg-green-50 border-green-200">
                <h4 className="font-bold text-green-700 mb-3">🟢 EFI (PIX)</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-sm">Taxa Gateway (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={gatewayFees.efi_fee_percentage}
                      onChange={(e) => setGatewayFees({...gatewayFees, efi_fee_percentage: e.target.value})}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">IOF (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={gatewayFees.efi_iof_percentage}
                      onChange={(e) => setGatewayFees({...gatewayFees, efi_iof_percentage: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Stripe PIX */}
              <div className="p-4 border rounded-lg bg-purple-50 border-purple-200">
                <h4 className="font-bold text-purple-700 mb-1">🟣 Stripe (PIX)</h4>
                <p className="text-xs text-purple-700 mb-3">
                  ⚠️ Medido na conta Stripe: <b>3,51% + 3,36% de IOF + 1% de câmbio ≈ 7,87%</b>.
                  Hoje o PIX roda pela EFI; só ajuste aqui se voltar a usar a Stripe para PIX.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-sm">Taxa Gateway (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={gatewayFees.stripe_fee_percentage}
                      onChange={(e) => setGatewayFees({...gatewayFees, stripe_fee_percentage: e.target.value})}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">IOF (%) - Absorvido</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={gatewayFees.stripe_iof_percentage}
                      onChange={(e) => setGatewayFees({...gatewayFees, stripe_iof_percentage: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* Mercado Pago */}
              <div className="p-4 border rounded-lg bg-sky-50 border-sky-200">
                <h4 className="font-bold text-sky-700 mb-1">🔵 Mercado Pago (PIX)</h4>
                <p className="text-xs text-sky-700 mb-3">
                  Taxa do Pix cobrada pelo Mercado Pago sobre o valor do pedido.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-sm">Taxa Gateway (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      data-testid="mercadopago-fee-pct-input"
                      value={gatewayFees.mercadopago_fee_percentage}
                      onChange={(e) => setGatewayFees({...gatewayFees, mercadopago_fee_percentage: e.target.value})}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">IOF (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      data-testid="mercadopago-iof-pct-input"
                      value={gatewayFees.mercadopago_iof_percentage}
                      onChange={(e) => setGatewayFees({...gatewayFees, mercadopago_iof_percentage: e.target.value})}
                    />
                  </div>
                </div>
              </div>

              {/* MB WAY */}
              <div className="p-4 border rounded-lg bg-indigo-50 border-indigo-200">
                <h4 className="font-bold text-indigo-700 mb-1">📱 MB WAY (Portugal)</h4>
                <p className="text-xs text-indigo-700 mb-3">
                  Esta taxa é <b>repassada ao cliente</b> na hora de gerar a cobrança em euro
                  (ela não sai do seu bolso). Medido na conta Stripe: <b>3,0% + € 0,25</b>
                  {' '}(1,5% MB WAY + 1,5% internacional, já sem o 1% de conversão porque
                  recebemos em euro).
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label className="text-sm">Taxa Gateway (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      data-testid="mbway-fee-pct-input"
                      value={gatewayFees.mbway_fee_percentage}
                      onChange={(e) => setGatewayFees({...gatewayFees, mbway_fee_percentage: e.target.value})}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Taxa Fixa (EUR)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      data-testid="mbway-fee-fixed-input"
                      value={gatewayFees.mbway_fee_fixed_eur}
                      onChange={(e) => setGatewayFees({...gatewayFees, mbway_fee_fixed_eur: e.target.value})}
                    />
                  </div>
                </div>
                {summary?.eur_brl_rate > 0 && (
                  <div className="mt-3 rounded-lg bg-white border border-indigo-200 p-2.5 text-xs"
                    data-testid="mbway-simulator">
                    <p className="font-bold text-indigo-900 mb-1">
                      Simulação (1 € = R$ {summary.eur_brl_rate.toFixed(2)}):
                    </p>
                    {[54, 159, 264].map((brl) => {
                      const pct = parseFloat(gatewayFees.mbway_fee_percentage || 0) / 100;
                      const fixo = parseFloat(gatewayFees.mbway_fee_fixed_eur || 0);
                      const base = brl / summary.eur_brl_rate;
                      const total = Math.ceil(((base + fixo) / (1 - pct)) * 100) / 100;
                      const taxa = total * pct + fixo;
                      return (
                        <p key={brl} className="text-gray-700">
                          Pacote R$ {brl.toFixed(2)} → cliente paga <b>€ {total.toFixed(2)}</b>
                          {' '}(taxa € {taxa.toFixed(2)} · {(100 * taxa / total).toFixed(1)}%) ·
                          {' '}sobra para nós R$ {((total - taxa) * summary.eur_brl_rate).toFixed(2)}
                        </p>
                      );
                    })}
                  </div>
                )}
              </div>

              <Button onClick={handleUpdateGatewayFees} className="w-full bg-blue-600 hover:bg-blue-700">
                Salvar Taxas
              </Button>

              {summary && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-bold text-sm text-blue-600 mb-2">Custo de gateway no período:</h4>
                  <div className="space-y-1 text-sm">
                    <p>Taxa de gateway: <strong>R$ {(summary.gateway_fees ?? 0).toFixed(2)}</strong></p>
                    <p>IOF: <strong>R$ {(summary.iof_total ?? 0).toFixed(2)}</strong></p>
                    <p>Total: <strong className="text-blue-600">R$ {(summary.total_fees ?? 0).toFixed(2)}</strong></p>
                <p className="text-xs text-gray-600 pt-1" data-testid="fees-source-note">
                  {summary.fees_real_count ?? 0} pedido(s) com a <b>taxa real</b> cobrada pelo
                  gateway e {summary.fees_estimated_count ?? 0} com estimativa pelos percentuais
                  acima. Pedidos novos gravam a taxa real automaticamente.
                  No MB WAY a taxa é paga pelo cliente, então só entra como custo aqui
                  o que sobrou faltando do valor do pacote (pedidos antigos).
                </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FinancesManagement;
